import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  BackgroundPollJob,
  SearchPollingService,
} from '../../../services/search-polling/search-polling.service';
import { toPercent } from '../../../services/search-polling/osint-job-poll-response.util';

const MINIMIZED_STORAGE_KEY = 'osint_bg_poll_minimized';

const SERVICE_LABELS: Record<string, string> = {
  username: 'Username',
  wallet: 'Wallet',
  email: 'Email',
  phone: 'Phone Number',
  name: 'Name',
  gst: 'GST',
  din: 'DIN',
  mobile_pan: 'Mobile PAN',
  'mobile-pan': 'Mobile PAN',
  phonepe_fastag: 'Fastag Search',
  'phonepe-fastag': 'Fastag Search',
  phonepe_upi: 'UPI to Bank',
  'phonepe-upi': 'UPI to Bank',
  basic: 'Basic Search',
  'basic-phone': 'Basic Search',
  'leak-phone': 'Leak Phone',
  'leak-email': 'Leak Email',
  'leak-username': 'Leak Username',
  'leak-password': 'Leak Password',
  'advanced-leak-phone': 'Adv. Leak Phone',
  'advanced-leak-email': 'Adv. Leak Email',
  pan: 'PAN',
  vehicle: 'Vehicle',
  advanced: 'Advanced Search',
};

const SERVICE_ROUTES: Record<string, string> = {
  username: '/osint-username-search',
  wallet: '/osint-wallet-search',
  email: '/osint-email-search',
  phone: '/osint-number-search',
  name: '/osint-name-search',
  gst: '/gst-search',
  din: '/din-search',
  mobile_pan: '/mobile-pan-search',
  'mobile-pan': '/mobile-pan-search',
  phonepe_fastag: '/phonepe-fastag-search',
  'phonepe-fastag': '/phonepe-fastag-search',
  phonepe_upi: '/phonepe-upi-search',
  'phonepe-upi': '/phonepe-upi-search',
  basic: '/basic-search',
  'basic-phone': '/basic-search',
  'leak-phone': '/leak-number-search',
  'leak-email': '/leak-email-search',
  'leak-username': '/leak-username-search',
  'leak-password': '/leak-password-search',
  'advanced-leak-phone': '/advanced-leak-number-search',
  'advanced-leak-email': '/advanced-leak-email-search',
  pan: '/pan-search',
  vehicle: '/vehicle-search',
  advanced: '/advanced-search',
};

@Component({
  selector: 'app-osint-background-poll-indicator',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './osint-background-poll-indicator.component.html',
  styleUrl: './osint-background-poll-indicator.component.css',
})
export class OsintBackgroundPollIndicatorComponent
  implements OnInit, OnDestroy
{
  jobs: BackgroundPollJob[] = [];
  minimized = false;
  private sub?: Subscription;

  constructor(
    private searchPollingService: SearchPollingService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.minimized = this.readMinimized();
    this.sub = this.searchPollingService.activeBackgroundJobs$.subscribe(
      (jobs) => {
        this.jobs = jobs || [];
      },
    );
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  get hasJobs(): boolean {
    return this.jobs.length > 0;
  }

  minimize(event?: Event) {
    event?.stopPropagation();
    this.minimized = true;
    this.persistMinimized(true);
  }

  expand() {
    this.minimized = false;
    this.persistMinimized(false);
  }

  getCaption(job: BackgroundPollJob): string {
    const percent = this.resolvePercent(job);
    // Include 0% so basic/search jobs with known progress stay in "Updating"
    // instead of looking stuck on Pending until the first platform finishes.
    if (job?.done || percent != null) return 'Updating…';
    return 'Pending';
  }

  getServiceLabel(job: BackgroundPollJob): string {
    const type = String(job?.searchType || '').toLowerCase();
    if (SERVICE_LABELS[type]) {
      return SERVICE_LABELS[type];
    }
    return job?.searchType ? this.toTitle(job.searchType) : 'OSINT Search';
  }

  hasProgress(job: BackgroundPollJob): boolean {
    return this.resolvePercent(job) != null;
  }

  getProgressLabel(job: BackgroundPollJob): string {
    const percent = this.resolvePercent(job);
    return percent != null ? `${percent}%` : '';
  }

  onJobClick(job: BackgroundPollJob) {
    if (!job?.job_id) return;
    const type = String(job.searchType || '').toLowerCase();
    const route = SERVICE_ROUTES[type];
    if (route) {
      this.router.navigate([route]);
    }
  }

  private resolvePercent(job: BackgroundPollJob): number | null {
    return toPercent(
      job?.progress?.percent,
      job?.progress_percent,
      job?.progress_milestone,
    );
  }

  private toTitle(value: string): string {
    return String(value)
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  }

  private readMinimized(): boolean {
    try {
      return sessionStorage.getItem(MINIMIZED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private persistMinimized(value: boolean) {
    try {
      sessionStorage.setItem(MINIMIZED_STORAGE_KEY, value ? '1' : '0');
    } catch {
      // ignore
    }
  }
}
